import React, { useCallback } from "react";
import * as Yup from "yup";
import { Form, Formik, useFormikContext } from "formik";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { styled } from '@mui/material/styles';
import { useNotifications } from "../../hooks/NotificationProvider";
import ClientStateSelectInput from "../classifier/ClientStateSelectInput";
import { translations } from "../../translations/common";

const StyledClientStateSelect = styled(ClientStateSelectInput)({
  marginBottom: 16,
});

interface Props {
  save(state: string): Promise<number>;
  close(): void;
}

interface StateFormValues {
  clientState: string;
}

const initialValues: StateFormValues = {
  clientState: "",
};

const validationSchema = Yup.object().shape({
  clientState: Yup.string().required(translations.clientState.required),
});

// This component connects Formik context to ClientStateSelectInput
const FormikClientStateSelect = () => {
  const { values, handleChange, handleBlur, errors, touched } = useFormikContext<StateFormValues>();
  return (
    <StyledClientStateSelect
      name="clientState"
      label={translations.clientState.label}
      fullWidth
      variant="outlined"
      value={values.clientState}
      onChange={handleChange}
      onBlur={handleBlur}
      errors={errors}
      touched={touched}
    />
  );
};

export default function ClientStateModalForm({ save, close }: Props) {
  const notifications = useNotifications();

  const handleSubmit = useCallback(async (values: StateFormValues) => {
    try {
      const count = await save(values.clientState);
      close();
      notifications.notify({
        level: "success",
        message: translations.clientState.success(count),
      });
    } catch (err: any) {
      notifications.notify({
        level: "error",
        message: err.data || translations.clientState.error,
      });
    }
  }, [save, close, notifications]);

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={close}>
      <DialogTitle>{translations.clientState.modalTitle}</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await handleSubmit(values);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <FormikClientStateSelect />
            </DialogContent>
            <DialogActions>
              <Button
                type="button"
                onClick={close}
                disabled={isSubmitting}
              >
                {translations.clientState.cancel}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                color="primary"
              >
                {translations.clientState.save}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
